import type { ReactNode } from 'react'
import { usePermissions } from '@/modules/auth/hooks/usePermissions'
import type { PermissionKey } from '@/modules/auth/permissions/permissions'

interface CanProps {
  /** Single permission required to render children */
  permission?: PermissionKey
  /** Multiple permissions — user needs at least ONE to render children */
  anyOf?: PermissionKey[]
  /** Multiple permissions — user needs ALL to render children */
  allOf?: PermissionKey[]
  /** Content to render when the user HAS the permission(s) */
  children: ReactNode
  /** Optional fallback content when the user does NOT have permission */
  fallback?: ReactNode
}

/**
 * Declarative wrapper to conditionally render UI based on permissions.
 * The children are only rendered if the current user has the required permission(s).
 * If no permission check is provided, children are always rendered.
 *
 * @example
 * ```tsx
 * // Single permission
 * <Can permission={Permission.CLIENTS_EXPORT}>
 *   <ExportButton />
 * </Can>
 *
 * // Any of multiple permissions
 * <Can anyOf={[Permission.CLIENTS_EDIT, Permission.CLIENTS_DELETE]}>
 *   <ActionMenu />
 * </Can>
 *
 * // All of multiple permissions
 * <Can allOf={[Permission.BENEFITS_VIEW, Permission.BENEFITS_EDIT]}>
 *   <EditBenefitButton />
 * </Can>
 *
 * // With fallback
 * <Can permission={Permission.CONFIG_EDIT} fallback={<ReadOnlyConfig />}>
 *   <EditableConfig />
 * </Can>
 * ```
 */
export function Can({ permission, anyOf, allOf, children, fallback = null }: CanProps) {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions()

  let allowed = true

  if (permission) {
    allowed = hasPermission(permission)
  } else if (anyOf && anyOf.length > 0) {
    allowed = hasAnyPermission(...anyOf)
  } else if (allOf && allOf.length > 0) {
    allowed = hasAllPermissions(...allOf)
  }

  if (!allowed) return <>{fallback}</>

  return <>{children}</>
}
